import { Document, Page, Text, View, Image, StyleSheet, pdf } from "@react-pdf/renderer";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const date = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—");
const bold = (font) => ({ Helvetica: "Helvetica-Bold", "Times-Roman": "Times-Bold", Courier: "Courier-Bold" }[font] || "Helvetica-Bold");

const makeStyles = (t) => StyleSheet.create({
  page: { padding: 40, fontSize: 9.5, fontFamily: t.font, color: "#111827" },
  bar: { height: 6, backgroundColor: t.accent_color, marginBottom: 20 },
  head: { flexDirection: "row", justifyContent: "space-between", marginBottom: 22 },
  logo: { width: 90, height: 40, objectFit: "contain", marginBottom: 6 },
  company: { fontFamily: bold(t.font), fontSize: 14, marginBottom: 3 },
  muted: { color: "#4B5563", marginBottom: 1.5 },
  title: { fontFamily: bold(t.font), fontSize: 20, color: t.accent_color, textAlign: "right" },
  metaRow: { flexDirection: "row", justifyContent: "flex-end", marginTop: 3 },
  metaLabel: { color: "#6B7280", width: 70, textAlign: "right", marginRight: 8 },
  metaVal: { fontFamily: bold(t.font), width: 90, textAlign: "right" },
  boxes: { flexDirection: "row", marginBottom: 20 },
  box: { flex: 1, borderWidth: 1, borderColor: "#E5E7EB", padding: 10, marginRight: 10 },
  boxLast: { flex: 1, borderWidth: 1, borderColor: "#E5E7EB", padding: 10 },
  boxLabel: { fontFamily: bold(t.font), fontSize: 8, color: t.accent_color, marginBottom: 5, letterSpacing: 1 },
  th: { flexDirection: "row", backgroundColor: "#111827", color: "#FFFFFF", paddingVertical: 6, paddingHorizontal: 6, fontFamily: bold(t.font), fontSize: 8 },
  tr: { flexDirection: "row", paddingVertical: 6, paddingHorizontal: 6, borderBottomWidth: 1, borderBottomColor: "#E5E7EB" },
  totals: { alignSelf: "flex-end", width: 200, marginTop: 10 },
  totalRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 3 },
  grand: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 6, borderTopWidth: 2, borderTopColor: t.accent_color, marginTop: 4, fontFamily: bold(t.font), fontSize: 12 },
  notes: { marginTop: 24, padding: 10, backgroundColor: "#F9FAFB", borderLeftWidth: 3, borderLeftColor: t.accent_color },
  sigWrap: { flexDirection: "row", marginTop: 40 },
  sig: { flex: 1, borderTopWidth: 1, borderTopColor: "#9CA3AF", paddingTop: 4, marginRight: 30, color: "#6B7280", fontSize: 8 },
  footer: { position: "absolute", bottom: 24, left: 40, right: 40, flexDirection: "row", justifyContent: "space-between", fontSize: 7.5, color: "#9CA3AF" },
});

function columns(t) {
  return [
    { k: "n", label: "#", w: 22, r: (l, i) => i + 1 },
    t.show_sku && { k: "sku", label: "PART #", w: 85, r: (l) => l.ext_sku || l.sku },
    { k: "name", label: "DESCRIPTION", flex: 1, r: (l) => l.name },
    t.show_packs && { k: "packs", label: "PACKS", w: 70, align: "right", r: (l) => (l.order_unit_qty > 1 ? `${num(l.packs)} × ${num(l.order_unit_qty)}` : "—") },
    { k: "qty", label: "QTY", w: 70, align: "right", r: (l) => `${num(l.qty)} ${l.unit}` },
    t.show_unit_cost && { k: "cost", label: "UNIT COST", w: 60, align: "right", r: (l) => money(l.unit_cost) },
    t.show_unit_cost && { k: "total", label: "AMOUNT", w: 65, align: "right", r: (l) => money(l.line_total) },
  ].filter(Boolean);
}

const cellStyle = (c) => ({ width: c.w, flex: c.flex, textAlign: c.align || "left", paddingRight: 4 });

export function PODocument({ po, template: t }) {
  const s = makeStyles(t);
  const cols = columns(t);
  const sup = po.supplier || {};
  return (
    <Document title={po.po_number} author={t.company_name}>
      <Page size={t.paper_size} style={s.page}>
        <View style={s.bar} fixed />
        <View style={s.head}>
          <View style={{ maxWidth: 260 }}>
            {t.logo_url ? <Image src={t.logo_url} style={s.logo} /> : null}
            <Text style={s.company}>{t.company_name}</Text>
            <Text style={s.muted}>{t.company_address}</Text>
            <Text style={s.muted}>{[t.company_phone, t.company_email].filter(Boolean).join("  ·  ")}</Text>
          </View>
          <View>
            <Text style={s.title}>{t.title}</Text>
            <View style={s.metaRow}><Text style={s.metaLabel}>PO NUMBER</Text><Text style={s.metaVal}>{po.po_number}</Text></View>
            <View style={s.metaRow}><Text style={s.metaLabel}>DATE</Text><Text style={s.metaVal}>{date(po.created_at)}</Text></View>
            {po.expected_date ? <View style={s.metaRow}><Text style={s.metaLabel}>NEEDED BY</Text><Text style={s.metaVal}>{date(po.expected_date)}</Text></View> : null}
            {t.payment_terms ? <View style={s.metaRow}><Text style={s.metaLabel}>TERMS</Text><Text style={s.metaVal}>{t.payment_terms}</Text></View> : null}
          </View>
        </View>
        <View style={s.boxes}>
          <View style={s.box}>
            <Text style={s.boxLabel}>VENDOR</Text>
            <Text style={{ fontFamily: bold(t.font), marginBottom: 2 }}>{sup.name || "No supplier assigned"}</Text>
            {sup.contact ? <Text style={s.muted}>Attn: {sup.contact}</Text> : null}
            {sup.email ? <Text style={s.muted}>{sup.email}</Text> : null}
            {sup.phone ? <Text style={s.muted}>{sup.phone}</Text> : null}
          </View>
          <View style={s.boxLast}>
            <Text style={s.boxLabel}>SHIP TO</Text>
            <Text style={s.muted}>{t.ship_to}</Text>
          </View>
        </View>
        <View style={s.th} fixed>{cols.map((c) => <Text key={c.k} style={cellStyle(c)}>{c.label}</Text>)}</View>
        {po.lines.map((l, i) => (
          <View key={l.item_id} style={s.tr} wrap={false}>{cols.map((c) => <Text key={c.k} style={cellStyle(c)}>{c.r(l, i)}</Text>)}</View>
        ))}
        {t.show_unit_cost && (
          <View style={s.totals}>
            <View style={s.totalRow}><Text style={s.muted}>Subtotal</Text><Text>{money(po.total)}</Text></View>
            <View style={s.grand}><Text>TOTAL</Text><Text>{money(po.total)}</Text></View>
          </View>
        )}
        {(t.footer_notes || po.notes) ? (
          <View style={s.notes} wrap={false}>
            <Text style={s.boxLabel}>{(t.notes_heading || "NOTES").toUpperCase()}</Text>
            {po.notes ? <Text style={{ marginBottom: 4 }}>{po.notes}</Text> : null}
            {t.footer_notes ? <Text style={s.muted}>{t.footer_notes}</Text> : null}
          </View>
        ) : null}
        {t.show_signature && (
          <View style={s.sigWrap} wrap={false}>
            <Text style={s.sig}>Authorized by</Text><Text style={s.sig}>Date</Text>
          </View>
        )}
        <View style={s.footer} fixed>
          <Text>{t.company_name} · {po.po_number}</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}

export const poBlob = (po, template) => pdf(<PODocument po={po} template={template} />).toBlob();

export const SAMPLE_PO = {
  po_number: "PO-SAMPLE-0001", created_at: new Date().toISOString(), expected_date: null, notes: "",
  supplier: { name: "Sample Supplier Co.", contact: "Jane Doe", email: "orders@supplier.example", phone: "555-0100" },
  lines: [
    { item_id: "a", sku: "RAW-FAB-100", name: "Cordura Fabric Roll", unit: "yd", order_unit_qty: 100, packs: 2, qty: 200, unit_cost: 9.5, line_total: 1900 },
    { item_id: "b", sku: "RAW-HW-BKL", name: "Side-Release Buckle 1in", unit: "count", order_unit_qty: 500, packs: 1, qty: 500, unit_cost: 0.4, line_total: 200 },
    { item_id: "c", sku: "RAW-THR-1", name: "Bonded Nylon Thread Spool", unit: "count", order_unit_qty: 1, packs: 6, qty: 6, unit_cost: 14, line_total: 84 },
  ],
  total: 2184,
};
